"use client";
import { config } from "@/lib/config";
import { motion } from "framer-motion";
import { DrupalNode } from "next-drupal";
import Image from "next/image";
import Link from "next/link";
import { resolveLink } from "@/utils";
import { MdChevronRight } from "react-icons/md";
import { usePathname } from "next/navigation";

interface CardProps {
  data: DrupalNode;
}

function BorderCard({ data }: CardProps) {
  const pathname = usePathname();
  const isMarketplaceTaskforce = pathname?.replace(/\/$/, "") === "/marketplace-taskforce";
  const isBlue = data?.field_background_color === "blue";
  const isWhite = data?.field_background_color === "white";
  const isGray = data?.field_background_color === "gray";
  const isDarkDray = data?.field_background_color === "dark_gray";

  const bgClass = isBlue
    ? "bg-[#003350]"
    : isWhite
      ? "bg-white"
      : isGray
        ? "bg-[#f9f9f9]"
        : isDarkDray
          ? "bg-mapGray"
          : "bg-white";

  return (
    <>
      {data?.field_title_position !== "left" ? (
        <motion.div
          className={`${bgClass} relative py-[120px] betweenMobileTab:py-16 mobileMax:py-10 overflow-hidden`}
        >
          <div className="mini-container z-[2] relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation "
            >
              <h3
                className={`mx-auto mobileMax:text-center ${
                  data?.field_title_position === "left"
                    ? "text-left"
                    : "text-center"
                } text-[42px] font-poppins mb-2 mobileMax:mb-8 font-semibold leading-normal text-transparent bg-clip-text bg-[linear-gradient(to_top,#8EBCE5,#003350)] text-clip mobileMax:text-[28px]`}
              >
                {data?.field_title}
              </h3>
              <motion.div
                className="mt-[70px] mobileMax:mt-[40px] mx-auto remove-animation-fluctuation flex flex-wrap items-stretch gap-[20px]"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.3,
                      delayChildren: 0.3,
                    },
                  },
                }}
              >
                {data?.field_add_card?.map((card: any, index: number) => {
                  const totalCards = data?.field_add_card?.length || 0;
                  const isLastOdd =
                    index === totalCards - 1 && totalCards % 2 !== 0;

                  const cardWidthClass = isMarketplaceTaskforce
                    ? "w-[calc(50%-15px)] desktop:w-[calc(25%-15px)] mobileMax:w-full"
                    : isLastOdd
                      ? "w-full"
                      : "w-[calc(50%-15px)] mobileMax:w-full";

                  return (
                    <motion.div
                      key={card.id || index}
                      className={`min-h-[300px] p-[20px] rounded-2xl flex justify-center items-stretch bg-[#003350] transition-transform duration-500 ease-in-out
					will-change-transform transform-gpu gro
					hover:scale-[1.03] group-hover:shadow-lg
				${cardWidthClass}`}
                    >
                      {/* Inner wrapper — this scales smoothly */}
                      <Link
                        href={resolveLink(card?.field_links?.[0]?.uri) || "#"}
                        className="group relative flex-1 rounded-2xl border-2 border-[#48DBB2] 
					bg-[linear-gradient(0deg,#226B8A_0%,#003350_40%)]
					overflow-hidden block
					"
                        style={{ transformOrigin: "center center" }}
                      >
                        {/* Actual content — stays stable, not scaled directly */}
                        <div className="flex flex-col justify-start items-center p-5 h-full">
                          {/* Image section */}
                          {card.field_icon?.uri?.url && (
                            <div
                              className={`flex items-end justify-center mb-4 shrink-0 ${isMarketplaceTaskforce ? "h-[50px]" : "h-[80px]"}`}
                            >
                              <Image
                                src={`${config.apiBase}${card.field_icon.uri.url}`}
                                alt={
                                  card.title || card.field_title || "Card icon"
                                }
                                // width={index === 0 ? 40 : 64}
                                // height={index === 0 ? 48 : 64}
                                width={isMarketplaceTaskforce ? 40 : 64}
                                height={isMarketplaceTaskforce ? 40 : 64}
                                className={`mx-auto block mobileMax:object-contain ${
                                  isMarketplaceTaskforce
                                    ? "w-[40px] h-[40px]"
                                    : index === 0
                                      ? // ? "w-[40px] h-[48px]"
                                        "w-[64px] h-[64px]"
                                      : "w-[64px] h-[64px]"
                                }`}
                              />
                            </div>
                          )}

                          {card.img && (
                            <div
                              className={`flex items-end justify-center mb-4 shrink-0 ${isMarketplaceTaskforce ? "h-[50px]" : "h-[80px]"}`}
                            >
                              <Image
                                src={card.img}
                                alt={card.title}
                                width={isMarketplaceTaskforce ? 40 : 64}
                                height={isMarketplaceTaskforce ? 40 : 64}
                                className={`mx-auto block mobileMax:w-[60px] mobileMax:object-contain ${
                                  isMarketplaceTaskforce
                                    ? "w-[40px] h-[40px]"
                                    : ""
                                }`}
                              />
                            </div>
                          )}

                          {/* Title */}
                          <h4 className="text-[22px] text-center font-poppins mb-3 font-bold leading-normal text-white mobileMax:text-[18px]">
                            {card.field_title || ""}
                          </h4>

                          {/* Description */}
                          <div
                            className="text-base text-center text-[#FFF6F6] bold-font-description leading-normal font-medium font-poppins mobileMax:text-sm"
                            dangerouslySetInnerHTML={{
                              __html: card.field_description?.processed || "",
                            }}
                          />
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </motion.div>
              {data?.field_button_link?.uri && (
                <div className="flex justify-center mt-10">
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={resolveLink(data?.field_button_link?.uri) || "#"}
                    className={`mobileMax:text-center mobileMax:w-full px-[40px] py-[15px] font-poppins inline-block ${
                      !isBlue ? "text-white" : "text-[#003350]"
                    } text-[16px] font-semibold tab:min-h-[50px] rounded-lg hover:!underline !no-underline betweenMobileTab:mx-auto mobileMax:text-small mobileMax:mb-3
                    ${
                      !isBlue
                        ? "bg-[linear-gradient(to_right,#316FA8,#003350)]"
                        : "bg-[linear-gradient(to_right,#48DBB2,#4FC0FF)]"
                    }
                  `}
                  >
                    {data?.field_button_link?.title}
                    <MdChevronRight className="text-[30px] ml-0.5 -mt-1 inline-block" />
                  </Link>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      ) : (
        <motion.div className="bg-[#f9f9f9] relative py-[120px] betweenMobileTab:py-16 mobileMax:py-10 overflow-hidden">
          <div className="mini-container z-[2] relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation "
            >
              <h3 className="mx-auto text-left text-[32px] font-poppins mb-[42px] mobileMax:mb-8 font-semibold leading-normal text-transparent bg-clip-text bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip mobileMax:text-[28px]">
                {data?.field_title}
              </h3>
              <motion.div
                className={`mx-auto remove-animation-fluctuation items-stretch gap-[22px] grid ${
                  isMarketplaceTaskforce
                    ? "desktop:grid-cols-4 betweenMobileTab:grid-cols-2 grid-cols-2 mobileMax:grid-cols-1"
                    : "betweenMobileTab:grid-cols-2 grid grid-cols-3 mobileMax:grid-cols-1"
                }`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.3,
                      delayChildren: 0.3,
                    },
                  },
                }}
              >
                {data?.field_add_card?.map((card) => (
                  <motion.div
                    key={card.id}
                    className="min-h-[300px] p-[20px] rounded-2xl flex justify-center items-stretch bg-[#003350] transition-transform duration-500 ease-in-out will-change-transform transform-gpu hover:scale-[1.03] group-hover:shadow-lg w-full"
                  >
                    <Link
                      href={resolveLink(card?.field_links?.[0]?.uri) || "#"}
                      className="group relative flex-1 border-2 border-[#48DBB2] bg-[linear-gradient(0deg,#1F6685_0%,#003350_40%)] overflow-hidden block"
                    >
                      <div
                        className={`flex flex-col justify-start items-center h-full ${isMarketplaceTaskforce ? "px-4 py-[35px]" : "px-8 py-[60px]"}`}
                      >
                        {card?.field_icon?.uri?.url && (
                          <div
                            className={`flex items-end justify-center mb-4 shrink-0 ${isMarketplaceTaskforce ? "h-[50px]" : "h-[80px]"}`}
                          >
                            <Image
                              src={`${config.apiBase}${card.field_icon.uri.url}`}
                              alt={
                                card.title || card.field_title || "Card icon"
                              }
                              width={isMarketplaceTaskforce ? 40 : 87}
                              height={isMarketplaceTaskforce ? 40 : 87}
                              className={`mx-auto block mobileMax:w-[60px] mobileMax:object-contain ${
                                isMarketplaceTaskforce
                                  ? "w-[40px] h-[40px]"
                                  : "w-full h-full"
                              }`}
                            />
                          </div>
                        )}
                        <h4 className="text-[22px] desktop:max-w-[195px] text-center font-poppins mb-3 font-bold leading-normal text-white mobileMax:text-[18px]">
                          {card?.field_title || ""}
                        </h4>
                        <p
                          className="text-small text-center text-[#FFF6F6] leading-normal font-medium font-poppins mobileMax:text-sm"
                          dangerouslySetInnerHTML={{
                            __html: card?.field_description?.processed || "",
                          }}
                        />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
              {data?.field_button_link?.uri && (
                <div className="flex justify-center mt-10">
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={resolveLink(data?.field_button_link?.uri) || "#"}
                    className={`mobileMax:text-center mobileMax:w-full px-[40px] py-[15px] font-poppins inline-block ${
                      !isBlue ? "text-white" : "text-[#003350]"
                    } text-[16px] font-semibold tab:min-h-[50px] rounded-lg hover:!underline !no-underline betweenMobileTab:mx-auto mobileMax:text-small mobileMax:mb-3
                    ${
                      !isBlue
                        ? "bg-[linear-gradient(to_right,#316FA8,#003350)]"
                        : "bg-[linear-gradient(to_right,#48DBB2,#4FC0FF)]"
                    }
                  `}
                  >
                    {data?.field_button_link?.title}
                    <MdChevronRight className="text-[30px] ml-0.5 -mt-1 inline-block" />
                  </Link>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      )}
    </>
  );
}

export default BorderCard;
